import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, useClerk } from '../contexts/TestAuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { usePermissions, Permissions } from '../contexts/PermissionsContext';
import { api } from '../utils/api';
import {
  Home,
  Plus,
  FileText,
  Clock,
  FolderOpen,
  PieChart,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  BarChart3,
  Scale,
  Menu,
  X,
  Briefcase,
  Bell,
  MessageSquare,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  Archive,
  Sparkles,
  Brain,
  RefreshCw
} from 'lucide-react';

export type NavRole = 'admin' | 'accountant' | 'data_specialist';

interface AppLayoutProps {
  children: React.ReactNode;
  navRole?: NavRole;
}

// Paths hidden per role (admin sees everything)
const NAV_HIDDEN: Record<NavRole, string[]> = {
  admin: [],
  accountant: ['/settings', '/queue/accountant'],
  data_specialist: [
    '/compare', '/analytics', '/vendor-intelligence',
    '/game-theory', '/settlement-intelligence',
    '/settings', '/queue/accountant', '/closed-cases',
  ],
};

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: string | number;
}

export default function AppLayout({ children, navRole }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { branding } = useBranding();
  const { hasPermission, role, roleDisplay, roleColor } = usePermissions();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [totalClaims, setTotalClaims] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  
  // AI Suggestions state
  const [aiSuggestionsCount, setAiSuggestionsCount] = useState(0);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [aiNeedsParsingCount, setAiNeedsParsingCount] = useState(0);
  const [parsingAll, setParsingAll] = useState(false);

  // Check permissions
  const canCreateClaims = hasPermission(Permissions.CLAIMS_CREATE);
  const canDeleteClaims = hasPermission(Permissions.CLAIMS_DELETE);
  const canExport = hasPermission(Permissions.REPORTS_EXPORT);

  // Fetch counts for badges from API
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=500', { showErrorToast: false });
        const reports = data.reports || data || [];
        setTotalClaims(reports.length);
        // Count pending (draft + in_progress)
        const pending = reports.filter((r: any) => r.status === 'draft' || r.status === 'in_progress').length;
        setPendingCount(pending);
        
        // Generate notifications from recent activity
        const recentNotifications: any[] = [];
        
        // Check for recently completed reports (last 24 hours)
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        reports.forEach((report: any) => {
          const updatedAt = new Date(report.updated_at || report.created_at);
          if (updatedAt > yesterday) {
            if (report.status === 'completed') {
              recentNotifications.push({
                id: `complete-${report.id}`,
                type: 'completed',
                title: 'Report Completed',
                message: `${report.client_name} calculation finished`,
                time: updatedAt,
                reportId: report.id
              });
            }
          }
        });
        
        // Sort by time and take most recent
        recentNotifications.sort((a, b) => b.time - a.time);
        setNotifications(recentNotifications.slice(0, 5));
        setNotificationCount(recentNotifications.length);
      } catch (error) {
        console.error('Error fetching sidebar counts:', error);
      }
    };
    fetchCounts();
  }, []);
  
  // Fetch AI suggestions count (poll every 60s)
  useEffect(() => {
    const fetchAiSuggestions = async () => {
      try {
        const data = await api.get('/api/v1/clarifications/pending-ai-suggestions?limit=10', { showErrorToast: false });
        const approvalItems = data.needs_approval || [];
        const parsingItems = data.needs_parsing || [];
        setAiSuggestionsCount(approvalItems.length);
        setAiSuggestions(approvalItems.slice(0, 5)); // Show top 5
        setAiNeedsParsingCount(parsingItems.length);
      } catch (error) {
        console.error('Error fetching AI suggestions:', error);
      }
    };
    
    fetchAiSuggestions();
    const interval = setInterval(fetchAiSuggestions, 60000); // Poll every 60s
    return () => clearInterval(interval);
  }, []);

  // Handle parse all from header dropdown
  const handleParseAllFromHeader = async () => {
    if (parsingAll) return;
    setParsingAll(true);
    
    try {
      await api.post('/api/v1/assistant/actions/parse-all-pending', null, { showErrorToast: false });
      
      // Refresh the data
      const data = await api.get('/api/v1/clarifications/pending-ai-suggestions?limit=10', { showErrorToast: false });
      setAiSuggestionsCount((data.needs_approval || []).length);
      setAiSuggestions((data.needs_approval || []).slice(0, 5));
      setAiNeedsParsingCount((data.needs_parsing || []).length);
    } catch (error) {
      console.error('Parse all error:', error);
    } finally {
      setParsingAll(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setAiDropdownOpen(false);
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Build nav items based on permissions
  const mainNavItems: NavItem[] = [
    { icon: Home, label: 'Dashboard', path: '/', badge: totalClaims },
    // Only show New Claim if user can create claims
    ...(canCreateClaims ? [{ icon: Plus, label: 'New Claim', path: '/wizard-v2' }] : []),
    { icon: Clock, label: 'Pending Review', path: '/?filter=pending', badge: pendingCount },
  ];

  const toolsNavItems: NavItem[] = [
    // Data Cleanup deprecated - features now in Wizard Step 3
    // { icon: FolderOpen, label: 'Data Cleanup', path: '/cleanup' },
    { icon: AlertCircle, label: 'Issue Tracker', path: '/issues' },
    { icon: Scale, label: 'Compare Claims', path: '/compare' },
    { icon: PieChart, label: 'Analytics', path: '/analytics' },
    { icon: Users, label: 'Vendor Intel', path: '/vendor-intelligence' },
    { icon: Brain, label: 'Game Theory', path: '/game-theory' },
    { icon: BarChart3, label: 'Settlement Intel', path: '/settlement-intelligence' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const resourceNavItems: NavItem[] = [
    { icon: FileText, label: 'Docs Guide', path: '/docs-guide' },
    { icon: Briefcase, label: 'Accountant Portal', path: '/queue/accountant' },
  ];

  const dataNavItems: NavItem[] = [
    { icon: Archive, label: 'Closed Cases', path: '/closed-cases' },
    { icon: HelpCircle, label: 'Review Queue', path: '/review-queue' },
  ];

  // Role-based nav filtering (when navRole is provided by RoleDashboardLayout)
  const hidden = navRole ? NAV_HIDDEN[navRole] : [];
  const filterNav = (items: NavItem[]) =>
    hidden.length > 0 ? items.filter((item) => !hidden.includes(item.path)) : items;

  const filteredToolsNav = filterNav(toolsNavItems);
  const filteredResourceNav = filterNav(resourceNavItems);
  const filteredDataNav = filterNav(dataNavItems);

  const isActive = (path: string) => {
    // Handle filter paths
    if (path === '/?filter=pending') {
      return location.search.includes('filter=pending');
    }
    // Dashboard is active when at / without pending filter
    if (path === '/') {
      return location.pathname === '/' && !location.search.includes('filter=pending');
    }
    return location.pathname.startsWith(path);
  };

  const NavItemButton = ({ item }: { item: NavItem }) => (
    <button
      onClick={() => {
        navigate(item.path);
        setMobileMenuOpen(false);
      }}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all ${
        isActive(item.path)
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
          : 'text-white/70 hover:bg-white/10 hover:text-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
        {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
      </div>
      {!sidebarCollapsed && item.badge !== undefined && (
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          isActive(item.path) ? 'bg-white/20' : 'bg-white/10'
        }`}>
          {item.badge}
        </span>
      )}
    </button>
  );

  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`
      ${mobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-60'}
      bg-[#0b2545] text-white transition-all duration-300 flex flex-col h-full
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <div 
          className="flex items-center justify-center cursor-pointer"
          onClick={() => navigate('/')}
          title="Go to Dashboard"
        >
          {sidebarCollapsed && !mobile ? (
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-10 w-10 object-contain hover:opacity-80 transition-opacity"
            />
          ) : (
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-44 w-auto max-w-[220px] object-contain hover:opacity-80 transition-opacity"
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {/* Main Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 text-xs font-semibold text-blue-300 uppercase tracking-wider">
            Main
          </p>
        )}
        <div className="space-y-1">
          {mainNavItems.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>

        {/* Tools Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 mt-6 text-xs font-semibold text-blue-300 uppercase tracking-wider">
            Tools
          </p>
        )}
        {sidebarCollapsed && !mobile && <div className="h-4" />}
        <div className="space-y-1">
          {filteredToolsNav.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>

        {/* Resources Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 mt-6 text-xs font-semibold text-blue-300 uppercase tracking-wider">
            Resources
          </p>
        )}
        {sidebarCollapsed && !mobile && <div className="h-4" />}
        <div className="space-y-1">
          {filteredResourceNav.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>

        {/* Data Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 mt-6 text-xs font-semibold text-blue-300 uppercase tracking-wider">
            Data
          </p>
        )}
        {sidebarCollapsed && !mobile && <div className="h-4" />}
        <div className="space-y-1">
          {filteredDataNav.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>
      </nav>

      {/* User Section */}
      {(!sidebarCollapsed || mobile) && (
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 cursor-pointer">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.firstName || 'User'}
              </p>
              <p className="text-xs text-white/50 truncate">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Collapse Toggle - Desktop only */}
      {!mobile && (
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-white/50 hover:bg-white/10 hover:text-white rounded-lg transition"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar mobile />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 p-2 text-white bg-white/20 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-40">
          {/* Left: Mobile menu + Page title area */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {/* AI Suggestions Badge */}
            {aiSuggestionsCount > 0 && (
              <div className="relative" data-dropdown>
                <button 
                  onClick={() => {
                    setAiDropdownOpen(!aiDropdownOpen);
                    setNotificationDropdownOpen(false);
                  }}
                  className="relative p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  aria-label={`${aiSuggestionsCount} pending AI category suggestions`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                    <circle cx="7.5" cy="14.5" r="1.5" />
                    <circle cx="16.5" cy="14.5" r="1.5" />
                  </svg>
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold text-white bg-blue-600 rounded-full">
                    {aiSuggestionsCount > 9 ? '9+' : aiSuggestionsCount}
                  </span>
                </button>
                
                {/* AI Suggestions Dropdown */}
                {aiDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-blue-200 z-50">
                    <div className="px-4 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50">
                      <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                        </svg>
                        AI Category Suggestions
                      </h3>
                      <p className="text-xs text-blue-600 mt-0.5">
                        {aiSuggestionsCount} response{aiSuggestionsCount !== 1 ? 's' : ''} ready for approval
                      </p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {aiSuggestions.map((item, idx) => (
                        <button
                          key={item.transaction_id || idx}
                          onClick={() => {
                            navigate(`/wizard-v2?draft=${item.claim_id}&step=3&section=transactions`);
                            setAiDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 hover:bg-blue-50 border-b border-gray-50 text-left"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {item.claimant_name || 'Unknown Claim'}
                              </p>
                              <p className="text-xs text-gray-500 truncate mt-0.5">
                                {item.description || item.transaction_id}
                              </p>
                            </div>
                            {item.amount && (
                              <span className={`text-sm font-medium flex-shrink-0 ${
                                item.amount < 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                ${Math.abs(item.amount).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                              {item.ai_parsed_category}
                            </span>
                            {item.ai_confidence && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                item.ai_confidence >= 0.9 ? 'text-green-700 bg-green-100' :
                                item.ai_confidence >= 0.7 ? 'text-yellow-700 bg-yellow-100' :
                                'text-red-700 bg-red-100'
                              }`}>
                                {item.ai_confidence >= 0.9 ? '🟢' : item.ai_confidence >= 0.7 ? '🟡' : '🔴'} {Math.round(item.ai_confidence * 100)}%
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="px-4 py-2 border-t border-blue-100 bg-gray-50 flex items-center justify-between">
                      <button 
                        onClick={() => {
                          navigate('/');
                          setAiDropdownOpen(false);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View All on Dashboard →
                      </button>
                      {aiNeedsParsingCount > 0 && (
                        <button
                          onClick={handleParseAllFromHeader}
                          disabled={parsingAll}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md font-medium flex items-center gap-1 disabled:opacity-50"
                        >
                          {parsingAll ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Parsing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3" />
                              Parse All ({aiNeedsParsingCount})
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Notifications */}
            <div className="relative" data-dropdown>
              <button 
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
              
              {/* Notification Dropdown */}
              {notificationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No new notifications</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => {
                            navigate(`/report/${notif.reportId}`);
                            setNotificationDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-50 text-left flex items-start gap-3"
                        >
                          <div className={`p-1.5 rounded-full flex-shrink-0 ${
                            notif.type === 'completed' ? 'bg-green-100' : 
                            notif.type === 'comment' ? 'bg-blue-100' : 'bg-yellow-100'
                          }`}>
                            {notif.type === 'completed' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : notif.type === 'comment' ? (
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-yellow-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                            <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(notif.time).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100">
                      <button 
                        onClick={() => {
                          setNotifications([]);
                          setNotificationCount(0);
                          setNotificationDropdownOpen(false);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* New Claim Button - Only show if user can create claims */}
            {canCreateClaims && (
              <button
                onClick={() => navigate('/wizard-v2')}
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-lg hover:shadow-xl transition-all"
              >
                <Plus className="w-4 h-4" />
                New Claim
              </button>
            )}

            {/* Role Badge */}
            <div 
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
            >
              {roleDisplay}
            </div>

            {/* Sign Out */}
            <button
              onClick={() => signOut()}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
